import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SearchBar from './SearchBar';
import AIBubble from './AIBubble';
import OffersSlider from './OffersSlider';
import AIChat from './AIChat';
import { useTranslation } from '../hooks/useTranslation';

const Home: React.FC = () => {
    const { t } = useTranslation();
    
    const features = [
        {
            icon: '🏨',
            title: 'فنادق تركيا المميزة',
            description: 'اكتشف أفضل الفنادق الفاخرة في إسطنبول، كابادوكيا، أنطاليا ومدن تركيا الساحرة'
        },
        {
            icon: '✈️',
            title: 'رحلات جوية مريحة',
            description: 'احجز رحلتك إلى تركيا بأفضل الأسعار مع شركات الطيران العالمية الموثوقة'
        },
        {
            icon: '🎯',
            title: 'وجهات تركية ساحرة',
            description: 'استكشف أجمل الوجهات في تركيا من إسطنبول التاريخية إلى كابادوكيا السحرية'
        },
        {
            icon: '🛡️',
            title: 'دعم على مدار الساعة',
            description: 'فريقنا المتخصص متاح 24/7 لمساعدتك في تخطيط رحلتك التركية المثالية'
        }
    ];

    const destinations = [
        { name: 'إسطنبول التاريخية', image: '/istanbul.jpg', price: 'من $85' },
        { name: 'كابادوكيا السحرية', image: '/cappadocia.jpg', price: 'من $120' },
        { name: 'أنطاليا الساحلية', image: '/antalya.jpg', price: 'من $180' },
        { name: 'باموكالي الطبيعية', image: '/pamukkale.jpg', price: 'من $100' }
    ];

    return (
        <div className="home">
            {/* Hero Section */}
            <section className="hero">
                <div className="container">
                    <motion.div 
                        className="hero-content"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="hero-title">
                            اكتشف المغامرة
                            <span className="gradient-text"> تركيا</span>
                        </h1>
                        <p className="hero-subtitle">
                            اكتشف جمال تركيا مع أفضل الفنادق والوجهات السياحية المميزة
                        </p>
                        
                        {/* AI Bubble */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3, duration: 0.6 }}
                        >
                            <AIBubble />
                        </motion.div>

                        {/* Search Bar */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.6 }}
                            className="hero-search"
                        >
                            <SearchBar />
                        </motion.div>

                        {/* CTA Buttons */}
                        <motion.div 
                            className="hero-cta"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7, duration: 0.6 }}
                        >
                            <Link to="/packages" className="btn btn-primary btn-lg">
                                استكشف الباقات
                            </Link>
                            <Link to="/destinations" className="btn btn-secondary btn-lg">
                                عرض الوجهات
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
                
                {/* Background Elements */}
                <div className="hero-bg">
                    <div className="floating-shapes">
                        <div className="shape shape-1"></div>
                        <div className="shape shape-2"></div>
                        <div className="shape shape-3"></div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features">
                <div className="container">
                    <motion.div 
                        className="section-header"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <h2>مميزاتنا الرائعة</h2>
                        <p>نقدم لك أفضل الخدمات السياحية المتميزة في تركيا</p>
                    </motion.div>

                    <div className="features-grid">
                        {features.map((feature, index) => (
                            <motion.div 
                                key={index}
                                className="feature-card card"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.6 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -5 }}
                            >
                                <div className="feature-icon">{feature.icon}</div>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Popular Destinations */}
            <section className="destinations">
                <div className="container">
                    <motion.div 
                        className="section-header"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <h2>الوجهات السياحية الشائعة</h2>
                        <p>اكتشف أجمل الوجهات السياحية المميزة في تركيا</p>
                    </motion.div>

                    <div className="destinations-grid">
                        {destinations.map((destination, index) => (
                            <motion.div 
                                key={index}
                                className="destination-card"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1, duration: 0.6 }}
                                viewport={{ once: true }}
                                whileHover={{ scale: 1.05 }}
                            >
                                <div className="destination-image">
                                    <div className="placeholder-image">
                                        <span>🌍</span>
                                    </div>
                                </div>
                                <div className="destination-content">
                                    <h3>{destination.name}</h3>
                                    <p className="destination-price">{destination.price}</p>
                                    <Link to={`/destination/${destination.name.toLowerCase()}`} className="btn btn-primary">
                                        Explore
                                    </Link>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Offers Section */}
            <section className="offers">
                <div className="container">
                    <motion.div 
                        className="section-header"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <h2>عروضنا الخاصة المميزة</h2>
                        <p>احصل على أفضل العروض والخصومات الحصرية</p>
                    </motion.div>
                    
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <OffersSlider />
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <motion.div 
                        className="cta-content"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <h2>ابدأ رحلتك المثالية الآن</h2>
                        <p>احجز رحلتك الحلم إلى تركيا مع أفضل الخدمات</p>
                        <div className="cta-buttons">
                            <Link to="/packages" className="btn btn-primary btn-lg">
                                ابدأ الآن
                            </Link>
                            <Link to="/contact" className="btn btn-secondary btn-lg">
                                اتصل بنا
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
            
            {/* AI Chat Widget */}
            <AIChat />
        </div>
    );
};

export default Home;
